import { useState } from "react";
import { useLang, fmtPrice } from "@/i18n";
import api, { errMsg } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const PACKAGES = [
  { id: "single", price: 60, lessons: 1 },
  { id: "pack5", price: 250, displayPrice: 50, lessons: 5, weekly: 2 },
];

export function BookingDialog({ open, onOpenChange, onCreated }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ package: "single", preferred_times: "", goal: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/bookings", form);
      toast.success(t("booking.success"));
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const pkg = PACKAGES.find((p) => p.id === form.package);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="booking-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{t("booking.title")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t("booking.choosePackage")}</Label>
            <div className="grid grid-cols-3 gap-2">
              {PACKAGES.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => setForm({ ...form, package: p.id })}
                  className={`rounded-lg border p-3 text-left transition-colors ${form.package === p.id ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border"}`}
                  data-testid={`booking-package-${p.id}`}
                >
                  <p className="text-sm font-bold">{t(`pricing.${p.id}.name`)}</p>
                  <p className="text-xs text-muted-foreground">{fmtPrice(p.displayPrice ?? p.price, lang)} {t("pricing.perLesson")}</p>
                  {p.weekly && <p className="text-xs font-semibold text-primary mt-0.5">{p.weekly} × = {fmtPrice(p.displayPrice * p.weekly, lang)} {t("booking.totalWeek")}</p>}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bk-times">{t("booking.preferred")}</Label>
            <Input id="bk-times" placeholder={t("booking.preferredPh")} value={form.preferred_times} onChange={(e) => setForm({ ...form, preferred_times: e.target.value })} data-testid="booking-times-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("booking.goal")}</Label>
            <Select value={form.goal || "general"} onValueChange={(v) => setForm({ ...form, goal: v })}>
              <SelectTrigger data-testid="booking-goal-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["citizenship", "residency", "job", "exam", "business", "general"].map((g) => (
                  <SelectItem key={g} value={g}>{t(`goals.${g}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bk-msg">{t("booking.message")}</Label>
            <Textarea id="bk-msg" rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="booking-message-input" />
          </div>
          <div className="rounded-lg bg-purple-50 border border-purple-200 p-3 text-sm text-purple-900" data-testid="booking-zelle-info">
            <p className="font-bold">{t("booking.zelleTitle")}</p>
            <p className="mt-1">{t("booking.zelleBody")} <strong>englishuniversestars@yahoo.com</strong></p>
            <p className="mt-1 text-xs">{t("booking.zelleMemo")}: {`{name}`} — {t(`pricing.${pkg.id}.name`)} ({fmtPrice(pkg.price, lang)} {t("booking.total")})</p>
          </div>
          <Button type="submit" className="w-full" size="lg" disabled={busy} data-testid="booking-submit-btn">
            {busy ? t("booking.sending") : `${t("booking.submit")} — ${fmtPrice(pkg.price, lang)} ${t("booking.total")}`}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
